import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '../store';
import dashboardService from '../../services/dashboardService';
import alertService from '../../services/alertService';

// Interface for critical alerts
export interface CriticalAlert {
  id: string;
  title: string;
  severity: 'critical' | 'high' | 'medium' | 'low';
  description: string;
  timestamp: string;
}

// Dashboard stats interface updated to match backend data structure
export interface DashboardStats {
  totalCharacters: number;
  divineOrbCount: number; // Changed from valuableItems
  activeBotsCount: number; // Updated to match service response
  activeAlertCount: number; // Changed from totalAlerts
}

interface DashboardState {
  stats: DashboardStats | null;
  criticalAlerts: CriticalAlert[];
  selectedGame: string | null; // Added to store selected game
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string | null | undefined;
}

const initialState: DashboardState = {
  stats: null,
  criticalAlerts: [],
  selectedGame: 'PoE1', // Default to PoE1
  status: 'idle',
  error: null,
};

// Async thunk for fetching dashboard statistics (using real API)
export const fetchDashboardStats = createAsyncThunk<DashboardStats, void, { state: RootState; rejectValue: string }>(
  'dashboard/fetchStats',
  async (_, thunkAPI) => {
    try {
      // Use the service to fetch real data with both game and league filters
      const apiStats = await dashboardService.getAllDashboardStats();

      // Transform API response to match our DashboardStats interface
      const dashboardStats: DashboardStats = {
        totalCharacters: apiStats.totalCharacters,
        divineOrbCount: apiStats.divineOrbCount,
        activeBotsCount: apiStats.activeBotsCount,
        activeAlertCount: apiStats.activeAlertCount,
      };

      return dashboardStats;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message || 'Failed to fetch dashboard stats');
    }
  },
);

// Async thunk for fetching critical alerts (now using real alerts API)
export const fetchCriticalAlertsSummary = createAsyncThunk<CriticalAlert[], void, { state: RootState; rejectValue: string }>(
  'dashboard/fetchCriticalAlertsSummary',
  async (_, thunkAPI) => {
    try {
      // Get selected game and league from state
      const state = thunkAPI.getState();
      const selectedGame = state.dashboard.selectedGame;
      const selectedLeague = state.filter.selectedLeague;

      // Use the alert service to fetch real alerts
      const alerts = await alertService.getAlerts(selectedGame || undefined, selectedLeague || undefined);

      // Filter to critical and high severity alerts and limit to 5
      const criticalAlerts = alerts
        .filter(alert => alert.severity === 'critical' || alert.severity === 'high')
        .slice(0, 5)
        .map(alert => ({
          id: alert.id,
          title: alert.title,
          severity: alert.severity as 'critical' | 'high' | 'medium' | 'low',
          description: alert.description || '',
          timestamp: alert.createdAt, // Use createdAt as timestamp
        }));

      return criticalAlerts;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message || 'Failed to fetch critical alerts');
    }
  },
);

const dashboardSlice = createSlice({
  name: 'dashboard',
  initialState,
  reducers: {
    clearDashboardError(state) {
      state.error = null;
      state.status = 'idle';
    },
    setSelectedGame(state, action: PayloadAction<string>) {
      state.selectedGame = action.payload;
      // Reset status to trigger a refetch when the game changes
      state.status = 'idle';
    },
  },
  extraReducers: builder => {
    builder
      // Fetch Dashboard Stats
      .addCase(fetchDashboardStats.pending, state => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchDashboardStats.fulfilled, (state, action: PayloadAction<DashboardStats>) => {
        state.status = 'succeeded';
        state.stats = action.payload;
      })
      .addCase(fetchDashboardStats.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })
      // Fetch Critical Alerts Summary
      .addCase(fetchCriticalAlertsSummary.pending, state => {
        // Could use a separate status for alerts if needed
        state.error = null;
      })
      .addCase(fetchCriticalAlertsSummary.fulfilled, (state, action: PayloadAction<CriticalAlert[]>) => {
        state.criticalAlerts = action.payload;
      })
      .addCase(fetchCriticalAlertsSummary.rejected, (state, action) => {
        state.error = action.payload; // Could use a separate error for alerts
      });
  },
});

export const { clearDashboardError, setSelectedGame } = dashboardSlice.actions;

// Selectors
export const selectDashboardStats = (state: RootState) => state.dashboard.stats;
export const selectCriticalAlertsSummary = (state: RootState) => state.dashboard.criticalAlerts;
export const selectDashboardStatus = (state: RootState) => state.dashboard.status;
export const selectDashboardError = (state: RootState) => state.dashboard.error;
export const selectSelectedGame = (state: RootState) => state.dashboard.selectedGame;

export default dashboardSlice.reducer;
